// Per-tier raid economics — the single shared model behind three consumers
// (ServerAuthoritySaveSecurity.md §4/§6):
//   1. Contract-completion payout (un-hired listing) — uses the *expected*
//      (average) per-raid values.
//   2. Class 3 sync rate ceiling (functions/_lib/rateBounds.js) — uses the
//      *max* per-raid values.
//   3. §6 payment amount cap (future) — max credits/raid × reward %.
//
// Pure, and shared by both the server (Pages Functions, via ../../js/modules/…)
// and the client (character.js withdraw reuses the same threshold, so the two
// can't disagree — one source of truth, no duplicated drift-prone math). A
// `*-core` module by the codebase convention (pure / simulator-safe): reads game
// data live from the generated js/constants.js and REPLICATES the small raid
// formulas from raid-core.js (calculateRaidResults / calculateRaidXP /
// calculateEnergyCost), which read gameState and so can't be imported into a
// Worker. The replication is kept honest by a drift-guard test
// (tests/server/raidEconomics.test.js) that runs the real functions and asserts
// equality — the same pattern characterValidation.js uses for the XP /
// skill-point curves. When a raid formula changes, that test fails and points
// here.
//
// Economy numbers (150, 40, 15, 3, 1.5, scaling multipliers, tier multipliers)
// are read live or replicated-with-drift-guard, so they never drift silently.
// The only hand-tuned values are the LAYOUT estimates below (path geometry and
// step mix) — these describe map shape, not economy, and are shared by every
// consumer so a single edit re-tunes them all.
import * as config from "../constants.js";

// --- Layout estimates (map geometry, not economy) --------------------------

// Average traversed path length for a tier = (rows + cols - 1) × this factor.
// Mirrors tools/loot-analysis-report.js (calculateRaidDuration).
export const PATH_LENGTH_FACTOR = 1.2;

// Fraction of path steps that are road (fast pass-through; not scored "rooms").
// Grounded in the grid generator: createRoadNetwork() (raid-core.js) targets
// ~15% of cells as road — a "sparse, strategic" network — so ~15% of a routed
// path is road and ~85% is rooms. (The offline loot tool assumed 40%, which
// over-counts roads: a player routing for loot/XP isn't aiming to walk roads.)
// THE single knob for the road/room split — drives both avg rooms per raid and
// avg raid duration below, so they can never disagree.
export const ROAD_FRACTION = 0.15;

// Representative gate / hazard step durations (seconds). Road/campsite/water are
// read live from commonAreas below; gates and hazards are consistently 120 / 60
// across regions, so they're pinned here with this note rather than scanned.
const GATE_SECONDS = 120;
const HAZARD_SECONDS = 60;

// Average duration of one room (non-road) step: campsite : water : gate : hazard
// blended 1 : 1 : 2 : 2 (the loot-analysis room proportions). road/campsite/water
// read live from commonAreas; gate/hazard pinned above.
function avgRoomStepSeconds() {
  const ca = config.commonAreas || {};
  const campsite = ca.campsite?.baseDuration ?? 120;
  const water = ca.water?.baseDuration ?? 120;
  return (campsite + water + 2 * GATE_SECONDS + 2 * HAZARD_SECONDS) / 6;
}

// Average duration of any path step — road and room blended by ROAD_FRACTION,
// the same knob that sets avg rooms per raid.
function avgStepSeconds() {
  const road = config.commonAreas?.road?.baseDuration ?? 10;
  return ROAD_FRACTION * road + (1 - ROAD_FRACTION) * avgRoomStepSeconds();
}

// --- Balance knobs ---------------------------------------------------------

// Contract-completion: share of listed time a character is assumed to actually
// be raiding (a hired-out character isn't on back-to-back raids the whole time).
// THE tuning knob for the un-hired consolation payout.
export const CONTRACT_UTILIZATION = 0.35;

// Contractors receive 80% of a raid's XP (raid-core.js finishRaidCore:
// contractorXP = floor(xpEarned × 0.8)).
export const CONTRACTOR_XP_SHARE = 0.8;

// Energy economy (character-core.js: 1 energy / 2 min regen; maxEnergy 100).
export const ENERGY_PER_HOUR = 30;
export const MAX_ENERGY = 100;

// --- Replicated raid formulas (drift-guarded) ------------------------------
// Success-branch of calculateRaidResults, WITHOUT the per-player currency-bonus
// artifact and full team survival — a baseline the server can compute for
// anyone. The recipient's own Contract Beacon still applies on collection.
function raidCreditsBase(tier, rooms) {
  const survivalBonus = 50; // floor(1.0 × 50), full survival
  return tier * 150 + rooms * 40 + survivalBonus;
}

// Credit scaling choke-point (raidUtil.js applyCreditsScaling) minus the
// per-player currency-bonus artifact. The runtime copies these multipliers into
// raidUtil via setCreditsScaling at init; the SSOT is the data below, which the
// server reads directly. (progressionScaling.creditsScaling, NOT a top-level
// config.creditsScaling — that doesn't exist and would silently no-op.)
function scaleCredits(creditsBase, tier) {
  const cs = config.progressionScaling?.creditsScaling || {};
  const global = cs.globalMultiplier ?? 1;
  const tierMult = cs.tierMultipliers?.[tier] ?? 1;
  return Math.floor(creditsBase * global * tierMult);
}

// calculateRaidXP, success branch. difficultyBonus assumes an adequate team
// (0); fullPathBonus assumes the path is completed (10). tierMult read live.
function raidXpValue(tier, rooms) {
  const baseXP = tier * 15;
  const roomBonus = rooms * 3;
  const fullPathBonus = 10;
  const tierMult = config.progressionScaling?.raidXpTierMultipliers?.[tier] || 1;
  return Math.max(5, Math.floor((baseXP + roomBonus + fullPathBonus) * 1.5 * tierMult));
}

// Energy cost for a raid (calculateEnergyCost) at baseline (no reduction
// artifact): base per tier + 1 energy / 2 min of duration.
function energyForRaid(tier, seconds) {
  const baseCost = 5 + (tier - 1) * 5;
  return baseCost + Math.ceil(seconds / 120);
}

// --- Public model ----------------------------------------------------------

export function avgPathLength(tier) {
  const t = config.raidTierData?.[tier];
  const rows = t?.gridRows ?? 7;
  const cols = t?.gridCols ?? 7;
  return (rows + cols - 1) * PATH_LENGTH_FACTOR;
}

// Expected (average) outcome of one raid at a tier.
export function expectedRaidOutcome(tier) {
  const path = avgPathLength(tier);
  const avgRaidSeconds = Math.round(path * avgStepSeconds());
  const avgRooms = Math.max(1, Math.round(path * (1 - ROAD_FRACTION)));
  return {
    avgRooms,
    avgRaidSeconds,
    expectedCredits: scaleCredits(raidCreditsBase(tier, avgRooms), tier),
    expectedXp: raidXpValue(tier, avgRooms),
    energyPerRaid: energyForRaid(tier, avgRaidSeconds),
  };
}

// Generous upper bound of one raid at a tier (every step scored a room) — for
// ceilings, not payouts.
export function maxRaidOutcome(tier) {
  const maxRooms = Math.ceil(avgPathLength(tier));
  return {
    maxRooms,
    maxCredits: scaleCredits(raidCreditsBase(tier, maxRooms), tier),
    maxXp: raidXpValue(tier, maxRooms),
  };
}

// Best XP-per-energy across tiers — a grinder optimizes for this, so it anchors
// the max sustained XP rate.
export function bestXpPerEnergy() {
  let best = 0;
  for (const tier of [1, 2, 3]) {
    const o = expectedRaidOutcome(tier);
    if (o.energyPerRaid > 0) best = Math.max(best, o.expectedXp / o.energyPerRaid);
  }
  return best;
}

// Honest energy-anchored max sustained XP/hour (before safety factor), given the
// max Command XP multiplier and the hired-contractor concurrency factor.
export function derivedMaxXpPerHour({ commandMult, contractorFactor }) {
  return ENERGY_PER_HOUR * bestXpPerEnergy() * commandMult * contractorFactor;
}

// One full energy pool spent at once (a player returning from idle).
export function derivedBurstXp({ commandMult, contractorFactor }) {
  return MAX_ENERGY * bestXpPerEnergy() * commandMult * contractorFactor;
}

// The tier a character is best suited to contract at: the highest tier whose
// contractor level band contains the level (bands overlap). Falls back to 1.
export function getAppropriateRaidTier(level) {
  const tiers = config.raidTierData || {};
  for (const tier of [3, 2, 1]) {
    const band = tiers[tier];
    if (!band) continue;
    const min = band.contractorMinLevel ?? 0;
    const max = band.contractorMaxLevel ?? 999;
    if (level >= min && level <= max) return tier;
  }
  return 1;
}

// Full un-hired contract-completion payout for a listing. Returns
// { qualifies, simulatedRaids, tier, credits, xp }. Does not touch the DB.
export function contractCompletionReward({ level, durationHours, rewardPercentage }) {
  const tier = getAppropriateRaidTier(level);
  const out = expectedRaidOutcome(tier);
  const raidingSeconds = (durationHours || 0) * 3600 * CONTRACT_UTILIZATION;
  const simulatedRaids = Math.floor(raidingSeconds / out.avgRaidSeconds);
  if (simulatedRaids < 1) {
    // Below the minimum: the listing didn't last one average raid's worth of
    // assumed raiding time.
    return { qualifies: false, simulatedRaids: 0, tier, credits: 0, xp: 0 };
  }
  const pct = rewardPercentage || 15;
  const credits = Math.floor(simulatedRaids * out.expectedCredits * pct / 100);
  const xp = Math.floor(simulatedRaids * out.expectedXp * CONTRACTOR_XP_SHARE);
  return { qualifies: true, simulatedRaids, tier, credits, xp };
}

// Minimum hours a listing must run before it earns one simulated raid's worth of
// completion payout, for a character of `level`. = avgRaidSeconds / utilization.
// Used client-side to warn on a withdrawal that happens before this point.
export function minContractHoursToEarn(level) {
  const out = expectedRaidOutcome(getAppropriateRaidTier(level));
  return out.avgRaidSeconds / (3600 * CONTRACT_UTILIZATION);
}
